import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { TableContainer, Paper, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import {
  SingleClaimTable,
  CLAIM_MODE_VIEW,
  CLAIM_MODE_EDIT,
  CLAIM_MODE_NEW_CLAIM,
  CLAIM_MODE_NEW_ENTITY,
} from '../components/claim/singleClaimTable';
import { TEAM_SCHEMA, TEAM_CLAIM_SCHEMA, MAPPED_TEAM_SCHEMA } from '../schema/teamSchema';
import { VIEW_TEAM_MAPPINGS, DELETE_TEAM_MAPPING } from '../hasura/queries/team/teamMapping';
import { ClaimFilters } from '../components/claim/claimFilters';
import ClaimHistoryTable from '../components/table/claimHistoryTable';
import { Progress } from '../components/util/progress';
import {
  GOLDEN_TEAM_AND_CLAIMS,
  SAVE_TEAM_CLAIM,
  OVERRIDE_TEAM_CLAIM,
  INSERT_GOLDEN_TEAM,
} from '../hasura/queries/team/teamClaims';
import { DELETE_TEAM, SIMILAR_TEAMS } from '../hasura/queries/team/teams';
import { Error } from '../components/util/notifications';
import { blankSchema } from '../util/schema';
import { LinkButton } from '../components/util/buttons';
import MappingView from '../components/mapping/mappingView';
import { SimilarEntity } from '../components/mapping/SimilarEntity';

const useStyle = makeStyles({
  root: {
    marginTop: 20,

    '& h3': {
      paddingLeft: 5,
    },
  },
});
const useStyleButton = makeStyles({
  button: { margin: 5, float: 'right' },
  clearButton: {
    '&:hover': {
      backgroundColor: '#DA0000',
    },
    backgroundColor: '#FF0000',
    color: 'white',
    height: 30,
    padding: 5,
    float: 'right',
    margin: 5,
  },
});

const GoldenTeamView = ({ teamId, isNewTeam, history }) => {
  const historyPath = history.location.search;

  const [claimFilter, setClaimFilter] = useState('all');
  const [selectedClaimId, setSelectedClaimId] = useState(null);
  const [claimStatuses, setClaimStatuses] = useState(isNewTeam ? blankSchema(TEAM_SCHEMA) : {});
  const [overrides, setOverrides] = useState({});
  const [claimMode, setClaimMode] = useState(isNewTeam ? CLAIM_MODE_NEW_ENTITY : CLAIM_MODE_VIEW);

  const setNewEntityMode = () => {
    setClaimMode(CLAIM_MODE_NEW_ENTITY);
    setClaimStatuses(blankSchema(TEAM_SCHEMA));
  };

  const setViewMode = () => {
    setClaimMode(CLAIM_MODE_VIEW);
    setClaimStatuses({});
  };

  useEffect(() => (historyPath === '?new=new' ? setNewEntityMode() : setViewMode()), [historyPath]);

  const tableContatinerClasses = useStyle();
  const buttonClasses = useStyleButton();
  const { loading, error, data, refetch } = useQuery(GOLDEN_TEAM_AND_CLAIMS, {
    variables: {
      team_id: teamId,
    },
  });

  if (loading) return <Progress />;
  let allClaims = [];

  data &&
    data.statsbomb_team &&
    data.statsbomb_team[0] &&
    data.statsbomb_team[0].mapping_teams.forEach(mappingEntry => {
      allClaims = [...allClaims, ...mappingEntry.claim_teams];
    });

  let filteredClaims = allClaims;
  if (claimFilter === 'undecided') {
    filteredClaims = allClaims.filter(c => {
      return c.approved_at === null && c.rejected_at === null;
    });
  } else if (claimFilter === 'approved') {
    filteredClaims = allClaims.filter(c => c.approved_at != null);
  } else if (claimFilter === 'rejected') {
    filteredClaims = allClaims.filter(c => c.rejected_at != null);
  }

  const selectAClaim = claimUUID => {
    setSelectedClaimId(claimUUID);
    const currentDecisions = {};

    const claim = allClaims.filter(c => {
      return c.claim_team_uuid === claimUUID;
    })[0];
    if (claim) {
      TEAM_SCHEMA.forEach(row => {
        if (claim && claim[row.columnKey] != null) {
          currentDecisions[row.columnKey] =
            row.mapping_function && row.mapping_function(claim) == null ? 'Ignored' : 'Approved';
        }
      });
    }
    setClaimStatuses(currentDecisions);

    const initialOverrides = {};
    TEAM_SCHEMA.forEach(row => {
      initialOverrides[row.columnKey] = null;
    });
    setOverrides(initialOverrides);

    setClaimMode(claimUUID ? CLAIM_MODE_EDIT : CLAIM_MODE_VIEW);
  };

  const changeClaimFilter = claimFilter => {
    selectAClaim(null);
    setClaimFilter(claimFilter);
  };

  const newClaim = () => {
    selectAClaim(null);
    setClaimMode(CLAIM_MODE_NEW_CLAIM);
    setClaimStatuses(blankSchema(TEAM_SCHEMA));
  };

  const getGoldenTeamId = insertedTeam => insertedTeam?.data?.insert_statsbomb_team?.returning?.[0]?.team_id;

  const claimSelected = claim => {
    selectAClaim(claim.claim_team_uuid);
  };

  return (
    <>
      <Error message="" error={error} />
      <div className="container-row">
        <Grid container spacing={3} justify="center" alignItems="center">
          <Grid item xs={6}>
            <h2>Team Singular: {isNewTeam ? ' New Team' : teamId}</h2>
          </Grid>
          <Grid item xs={6}>
            {claimMode === CLAIM_MODE_VIEW && (
              <Link
                to={{
                  pathname: `/team-squad`,
                  search: `?team_id=${teamId}`,
                }}
                component={LinkButton}
                label="View Squad"
              />
            )}
          </Grid>
        </Grid>

        {claimMode !== CLAIM_MODE_NEW_ENTITY && (
          <TableContainer component={Paper}>
            <ClaimFilters claimFilter={claimFilter} filterResultsFn={changeClaimFilter} />
            <ClaimHistoryTable
              entityType="team"
              schema={TEAM_CLAIM_SCHEMA}
              entities={filteredClaims}
              identifier={{ headerValue: 'Team Name', bodyValue: data.statsbomb_team[0]?.team_name }}
              rowClickHandler={claimSelected}
              selectedEntityKey={selectedClaimId}
            />
            <Button className={buttonClasses.button} variant="contained" color="primary" onClick={() => newClaim()}>
              New Claim
            </Button>
          </TableContainer>
        )}
        {claimMode !== CLAIM_MODE_NEW_ENTITY && (
          <MappingView
            type="team"
            recordId={teamId}
            schema={MAPPED_TEAM_SCHEMA}
            subscription={VIEW_TEAM_MAPPINGS}
            deleteMutation={DELETE_TEAM_MAPPING}
            deleteEntityMutation={DELETE_TEAM}
            entityKey="provider_team_id"
            styles={{ tableContatinerClasses, buttonClasses }}
          />
        )}
        <TableContainer classes={tableContatinerClasses} component={Paper}>
          <h3>Team Attributes {selectedClaimId && ' And Claim Values'}</h3>

          <SingleClaimTable
            goldenEntity={data.statsbomb_team[0]}
            claim={
              allClaims.filter(c => {
                return c.claim_team_uuid === selectedClaimId;
              })[0]
            }
            rowSchema={TEAM_SCHEMA}
            saveMutation={SAVE_TEAM_CLAIM}
            overrideMutation={OVERRIDE_TEAM_CLAIM}
            createMutation={INSERT_GOLDEN_TEAM}
            sourcePK="provider_team_id"
            claimPK="claim_team_uuid"
            goldenEntityPK="team_id"
            getGoldenEntityId={getGoldenTeamId}
            entityType="team"
            setClaimModeView={() => setClaimMode(CLAIM_MODE_VIEW)}
            similarEntityTable={SimilarEntity}
            similarEntityQuery={SIMILAR_TEAMS}
            similarEntityQueryObject="statsbomb_similar_teams"
            {...{
              claimStatuses,
              setClaimStatuses,
              selectAClaim,
              overrides,
              setOverrides,
              claimMode,
              refetch,
            }}
          />
        </TableContainer>
      </div>
    </>
  );
};

const Team = ({ history }) => {
  const query = new URLSearchParams(useLocation().search);
  const isNewTeam = query.get('new');
  const teamId = isNewTeam ? '0' : query.get('team_id');

  return <GoldenTeamView isNewTeam={!!isNewTeam} {...{ teamId, history }} />;
};

export default Team;
